<script setup lang="ts">
import type { PropType } from "vue";
import type { Collection } from "~/types";

const props = defineProps({
  video: {
    type: Object as PropType<Collection & { onAir: boolean }>,
    required: true,
  },
});

const prevVideoId = computed(() => {
  return props.video?.pre_video?.id;
});

const nextVideoId = computed(() => {
  return props.video?.post_video?.id;
});

const nextVideoOnAir = computed(() => {
  const now = new Date();
  const nextVideoOnAirDate = props.video?.post_video?.aired_at;
  return nextVideoOnAirDate ? new Date(nextVideoOnAirDate) <= now : false;
});

function playVideoById(id: string) {
  navigateTo(`/videos/${id}`);
}

defineExpose({
  autoPlayNext,
});

const autoPlayProgress = ref(0);

async function autoPlayNext() {
  if (nextVideoId.value) {
    while (autoPlayProgress.value < 100) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      autoPlayProgress.value += 1;
    }
    playVideoById(nextVideoId.value);
  }
}
</script>

<template>
  <div v-if="prevVideoId || nextVideoId" class="mt-5 flex justify-between">
    <Button
      v-if="prevVideoId"
      class="w-32 md:w-44"
      size="small"
      severity="white"
      @click="playVideoById(prevVideoId)"
    >
      <div class="flex items-center justify-center gap-2">
        <font-awesome-icon class="rotate-180" :icon="['fas', 'play']" />
        <span class="hidden md:block">Previous Episode</span>
        <span class="block md:hidden">Previous</span>
      </div>
    </Button>

    <Button
      v-if="nextVideoId"
      class="relative w-32 overflow-hidden md:w-44"
      icon-pos="right"
      size="small"
      severity="white"
      @click="playVideoById(nextVideoId)"
    >
      <div
        class="absolute inset-0 bg-surface-900/30 transition-all duration-100 ease-linear"
        :style="{
          width: `${autoPlayProgress}%`,
          left: '0',
          top: '0',
          height: '100%',
        }"
      />

      <div class="relative z-10 flex items-center justify-center gap-2">
        <span class="hidden md:block">
          {{ nextVideoOnAir ? "Next Episode" : "Upcoming Episode" }}
        </span>
        <span class="block md:hidden">
          {{ nextVideoOnAir ? "Next" : "Upcoming" }}
        </span>
        <font-awesome-icon :icon="['fas', 'play']" />
      </div>
    </Button>
  </div>
</template>

<style scoped></style>
